import { defaultText } from '@phaser/helpers/Text';
import { getLootdropData } from '@helpers/drops';
import { MessageEmitter } from '@phaser/events';
import { getItemHex } from '@helpers/items';
import { DEPTH } from '@phaser/data/depth';
import { Game } from '@phaser/scenes/Game';
import * as Shared from '@shared/lib';

const TIMEOUT = 2000;

export class ItemCollectInfoSystem {
  private _stack: {
    message: Shared.LootDropMessage;
    text?: Phaser.GameObjects.Text;
    bg?: Phaser.GameObjects.Image;
    untilClose: number;
    y: number;
  }[] = [];

  private _scene: Game = Game.current;

  public itemCollected(message: Shared.LootDropMessage) {
    MessageEmitter.emit('itemDrop', message);

    const alreadyInStack = this._stack.find(s =>
      s.message.type === message.type
      && s.message.id === message.id
    );

    if (alreadyInStack) {
      alreadyInStack.message.amount += message.amount;
      alreadyInStack.untilClose = TIMEOUT;

      if (alreadyInStack.text) {
        this._scene.add.tween({
          targets: alreadyInStack.text,
          ease: 'Sine.easeInOut',
          duration: 100,
          scale: 1.2,
          yoyo: true,
        });
      }

      return this._redraw();
    }

    this._stack.forEach(s => s.y += 20);
    this._stack.push({ untilClose: TIMEOUT, y: 10, message });

    this._redraw();
  }

  public update(_time: number, delta: number) {
    this._checkTimeouts(delta);
  }

  private _redraw() {
    const camera = this._scene.cameras.main;
    const height = camera.displayHeight;
    const width = camera.displayWidth;

    this._stack.forEach(s => {
      if (!s.text) {
        s.bg = this._scene.add.image(width, height - s.y, 'sprites', 'itemInfoBackground')
          .setAlpha(0.1)
          .setScale(-1, 0.4)
          .setOrigin(0, 0.5)
          .setDepth(DEPTH.ITEM_DROP_INFO - 1);
        s.text = this._scene.add.text(width - 5, height - s.y, '', {
            ...defaultText,
            strokeThickness: 0,
            fontSize: '12px',
          })
          .setOrigin(1, 0.5)
          .setDepth(DEPTH.ITEM_DROP_INFO);
      }

      this._scene.add.tween({
        targets: [s.text, s.bg],
        y: height - s.y,
        ease: 'Power2',
        duration: 250,
      });

      const message = getLootdropData(s.message);
      if (!message) return;

      const amount = ['health', 'energy'].includes(message.key)
        ? ''
        : `${message.amount} `;

      if (message.key === 'health') {
        s.text.setColor('#FF0000');
      } else if (message.key === 'energy') {
        s.text.setColor('#DEF141');
      } else {
        s.text.setColor(getItemHex(message));
      }

      s.text.setText(`${amount}${message.name}`);
    });
  }

  private _checkTimeouts(delta: number) {
    this._stack.forEach(s => { s.untilClose -= delta; });
    this._stack.filter(s => s.untilClose <= 0).forEach(s => {
      this._scene.add.tween({
        onComplete: () => {
          s.text?.destroy();
          this._stack = this._stack.filter(ss => ss !== s);
        },
        targets: [s.text, s.bg],
        ease: 'Power2',
        duration: 500,
        alpha: 0,
      });
    });
    this._stack = this._stack.filter(s => s.untilClose > 0);
  }
}